import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/icon/index.js';
import { cn } from '../../lib/utils.js';
import { Button } from '../design-system/primitives/Button/Button.js';
import { GatorMark } from './GatorMark.js';

export type MobileNavVariant = 'landing' | 'app';

export function mobileNavHeaderHeightClass(variant: MobileNavVariant): string {
  return variant === 'landing' ? 'h-[60px]' : 'h-16';
}

/** Inner row — must match NavBar mobile header exactly. */
export const MOBILE_NAV_ROW_INNER =
  'mx-auto flex h-full w-full max-w-[1200px] items-center justify-between gap-4 px-5 md:px-8';

export const MOBILE_NAV_LOGO_TEXT =
  'font-sans text-[18px] font-normal leading-tight tracking-tight text-ig-text-primary';

export const MOBILE_NAV_TOGGLE_CLASS =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-ig-border text-ig-text-primary';

type MobileNavLogoProps = {
  homeTo: string;
  className?: string;
};

export function MobileNavLogo({ homeTo, className }: MobileNavLogoProps) {
  return (
    <Link to={homeTo} className={cn('flex shrink-0 items-center gap-3', className)}>
      <GatorMark className="h-8 w-8 shrink-0 rounded-md" alt="" />
      <span className={MOBILE_NAV_LOGO_TEXT}>InterviewGator</span>
    </Link>
  );
}

type MobileNavToggleButtonProps = {
  isOpen: boolean;
  onClick: () => void;
  ariaLabel: string;
  /** Landing navbar uses lighter border on the page header; menu overlay always uses ig-border. */
  tone?: 'landing' | 'overlay';
};

export function MobileNavToggleButton({
  isOpen,
  onClick,
  ariaLabel,
  tone = 'overlay',
}: MobileNavToggleButtonProps) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className={cn(
        MOBILE_NAV_TOGGLE_CLASS,
        'bg-transparent p-0 hover:bg-transparent',
        tone === 'landing' && 'border-white/15 text-white',
      )}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {isOpen ? (
        <Icon name="x" className="h-5 w-5" />
      ) : (
        <span className="flex flex-col items-center gap-1.5" aria-hidden>
          <span className="h-[1.5px] w-5 bg-current" />
          <span className="h-[1.5px] w-5 bg-current" />
          <span className="h-[1.5px] w-4 bg-current" />
        </span>
      )}
    </Button>
  );
}

type MobileNavHeaderShellProps = {
  variant: MobileNavVariant;
  border?: boolean;
  children: ReactNode;
};

export function MobileNavHeaderShell({ variant, border = true, children }: MobileNavHeaderShellProps) {
  return (
    <div
      className={cn(
        'flex w-full shrink-0 bg-ig-bg-primary',
        mobileNavHeaderHeightClass(variant),
        border && 'border-b border-[#1F1F1F]',
      )}
    >
      <div className={MOBILE_NAV_ROW_INNER}>{children}</div>
    </div>
  );
}
